import { useCallback, useEffect, useRef, useState } from 'react';

const DEBOUNCE_MS = 450;

// Debounced live request for the J1939 Frame Decoder, with the same behaviour as the CAN Raw
// Frame Decoder: stale responses are dropped, and on HTTP 429 the last results stay on screen
// while a countdown runs, then the newest inputs are sent.
//
// `context` (e.g. the frame/SPN ids at request time) is handed back with the response so it
// can be mapped onto the right cards even if rows were added or removed meanwhile.
export default function useLiveDecode(api, payloadKey, context, onResult) {
  const [loading, setLoading] = useState(false);
  const [requestError, setRequestError] = useState(null);
  const [pausedForS, setPausedForS] = useState(0);
  const requestIdRef = useRef(0);
  const pausedUntilRef = useRef(0);
  const retryTimerRef = useRef(null);
  const latestRef = useRef({ key: payloadKey, context });
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;

  const request = useCallback((key, ctx) => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    return api.run(JSON.parse(key))
      .then((res) => {
        if (requestId !== requestIdRef.current) return null;
        setLoading(false);
        if (res.rateLimited) {
          pausedUntilRef.current = Date.now() + res.retryAfterS * 1000;
          setPausedForS(res.retryAfterS);
          clearTimeout(retryTimerRef.current);
          retryTimerRef.current = setTimeout(() => {
            pausedUntilRef.current = 0;
            setPausedForS(0);
            request(latestRef.current.key, latestRef.current.context);
          }, res.retryAfterS * 1000);
          return null;
        }
        if (res.errors) {
          setRequestError(res.error || Object.values(res.errors)[0]);
          return null;
        }
        setRequestError(null);
        return onResultRef.current(res.results, ctx);
      })
      .catch((e) => {
        if (requestId !== requestIdRef.current) return null;
        setLoading(false);
        setRequestError(e.message);
        return null;
      });
  }, [api]);

  const contextKey = JSON.stringify(context);
  useEffect(() => {
    const ctx = JSON.parse(contextKey);
    latestRef.current = { key: payloadKey, context: ctx };
    if (Date.now() < pausedUntilRef.current) return undefined;
    const timer = setTimeout(() => request(payloadKey, ctx), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [payloadKey, contextKey, request]);

  useEffect(() => {
    if (pausedForS <= 0) return undefined;
    const t = setTimeout(() => setPausedForS((v) => Math.max(0, v - 1)), 1000);
    return () => clearTimeout(t);
  }, [pausedForS]);

  useEffect(() => () => clearTimeout(retryTimerRef.current), []);

  const requestNow = () => {
    if (Date.now() < pausedUntilRef.current) return Promise.resolve({ paused: true });
    return request(latestRef.current.key, latestRef.current.context);
  };

  return { loading, requestError, pausedForS, requestNow };
}
